<template>
    <div>
        <div
            class="border bg-white rounded-lg w-full p-4 shadow-sm max-w-4xl mt-3 mb-3"
        >
            <p class="text-gray-700 mb-4">Copy Content Blocks From</p>
            <polymorphic-reference
                :creatable="false"
                :deletable="true"
                :linkable="true"
                :language="language"
                v-model="contentBlocksCopiedFrom"
                :models="copiableTypes"
                :should-open-new-window=true
            />
        </div>

        <div class="flex justify-end">
            <input
                :disabled="disabled"
                class="mr-2 leading-tight form-checkbox"
                type="checkbox"
                id="collapse_all"
                v-model="contentBlocksCollapsed"
                @change="toggleAllContentBlocksCollapse()"
            />
            <label for="collapse_all" class="text-gray-700"
                >Show All Blocks</label
            >
        </div>
        <div
            v-if="displayNoContentBlocksMessage"
            class="border bg-white rounded-lg w-full p-4 shadow-sm max-w-4xl mt-3 mb-3 text-yellow-600">
            No Content Blocks Added
        </div>
        <draggable
            :value="contentBlocks"
            @input="(data) => $emit('input', data)"
            handle=".handle"
            ghost-class="opacity-50"
            @choose="froalaStore.disableFroala = true"
            @unchoose="froalaStore.disableFroala = false"
        >
            <div
                v-for="(contentBlock, index) in contentBlocks"
                :key="`key_content_block_${index}`"
                class="my-2 bg-white rounded-lg shadow relative"
            >
                <div class="flex justify-between items-center cursor-pointer hover:bg-gray-50 p-4"
                     @click="toggleContentBlockCollapse(index)">
                    <div class="handle">
                        <svg class="w-6 h-6 inline-block" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16"></path></svg>

                        <span v-if="debugStore.debugMode && contentBlock.id" class="font-mono">
                            [ContentBlock:{{ contentBlock.id }}]
                        </span>
                    </div>
                    <div>
                        {{ contentBlock.title }}
                    </div>
                    <div>
                        <svg v-if="!contentBlock.collapsed" class="h-4 w-4 cursor-pointer" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                        </svg>
                        <svg v-if="contentBlock.collapsed" class="h-4 w-4 cursor-pointer" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 15l7-7 7 7" />
                        </svg>
                    </div>
                </div>
                <div class="px-4 pb-4" v-show="contentBlock.collapsed">
                    <div class="relative h-6">
                        <button
                            :disabled="disabled"
                            type="button"
                            class="absolute top-0 right-0 text-white flex flex-row items-center rounded px-3 py-2"
                            :class="
                                disabled
                                    ? 'bg-red-300 cursor-not-allowed'
                                    : 'bg-red-500'
                            "
                            @click="$emit('delete-content-block', index)"
                        >
                            <svg
                                class="w-4 h-4"
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                            >
                                <path
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    stroke-width="2"
                                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                />
                            </svg>
                            <span class="ml-2">Delete H2 Block</span>
                        </button>
                    </div>
                    <content-block
                        :disabled="disabled"
                        :gallery="gallery"
                        :language="language"
                        v-model="value[index]"
                        :content-block-index="index"
                        :errors="errors"
                        :should-show-market-data="shouldShowMarketData"
                        :additional-casinos-component="additionalCasinosComponent"
                        :is-payment-category-page="isPaymentCategoryPage"
                        :payment-method-id="paymentMethodId"
                        :show-all-content="contentBlocksCollapsed"
                        :content-section-with-how-to="contentSectionWithHowTo"
                        :content-section-with-bonus-list-top="contentSectionWithBonusListTop"
                        :content-section-with-bonus-list-bottom="contentSectionWithBonusListBottom"
                        :content-sections-with-ranking="contentSectionsWithRanking"
                        :document-class="documentClass"
                        :document-id="documentId"
                        :document-data-id="documentDataId"
                        :document-type-id="documentTypeId"
                        :has-bonus-lists="hasBonusLists"
                        :has-ranking="hasRanking"
                        @add-content-section="
                            $emit('add-content-section', index)
                        "
                        @delete-content-section="
                            (data) => $emit('delete-content-section', data)
                        "
                        @add-how-to="(uid) => $emit('add-how-to', uid)"
                        @delete-how-to="(uid) => $emit('delete-how-to', uid)"
                        @add-bonus-list-top="(uid) => $emit('add-bonus-list-top', uid)"
                        @delete-bonus-list-top="(uid) => $emit('delete-bonus-list-top', uid)"
                        @add-bonus-list-bottom="(uid) => $emit('add-bonus-list-bottom', uid)"
                        @delete-bonus-list-bottom="(uid) => $emit('delete-bonus-list-bottom', uid)"
                        @add-ranking="(uid) => $emit('add-ranking', uid)"
                        @delete-ranking="(uid) => $emit('delete-ranking', uid)"
                    />
                </div>
            </div>
        </draggable>
        <button
            :disabled="disabled"
            type="button"
            @click="$emit('add-content-block')"
            class="mt-2 mb-4"
            :class="disabled ? 'cursor-not-allowed' : null"
        >
            Add New H2
        </button>
    </div>
</template>

<script>
import draggable from "vuedraggable";
import siteLanguage from '../../config/siteLanguage'
import {useDebugStore} from "../../stores/debug";
import {useFroalaStore} from "../../stores/froala";

export default {
    name: "BodyContent",
    components: { draggable },
    props: {
        gallery: Array,
        value: {
            type: Array,
            required: true,
        },
        errors: {
            type: [Array, Object],
            required: true,
        },
        language: {
            type: Object,
            default: () => {
                return { iso: siteLanguage.iso.ENGLISH };
            },
        },
        documentClass: {
            type: String,
            required: true,
        },
        documentId: {
            type: Number,
            required: false,
        },
        documentDataId: {
            type: Number,
            required: false,
        },
        documentTypeId: {
            type: Number,
            required: false,
        },
        hasBonusLists: {
            type: Boolean,
            required: false,
            default: true,
        },
        hasRanking: {
            type: Boolean,
            required: false,
            default: true,
        },
        shouldShowMarketData: {
           type : Boolean,
           default : false
        },
        additionalCasinosComponent: {
            type: Object,
            default: () => {
                return { shouldShow: false };
            },
        },
        isPaymentCategoryPage: {
            type: Boolean,
            default: false,
        },
        paymentMethodId: {
            type: Number,
            required: false,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        copiableTypes: {
            type: Array,
            default: () => [
                "Category",
                "Software Category",
                "Payment Category",
                "License Category",
                "Country Category",
                "Bonus Type Category",
                "Game Type Category",
                "Casino Review",
                "Slot Review",
                "Game Review",
                "Payment Method Main Page",
                "License Main Page",
                "Knowledge Hub Page",
                "Blog Main Page",
                "Guide Main Page",
            ],
        },
        contentSectionWithHowTo: {
            type: String,
            required: false,
        },
        contentSectionWithBonusListTop: {
            type: String,
            required: false,
        },
        contentSectionWithBonusListBottom: {
            type: String,
            required: false,
        },
        contentSectionsWithRanking: {
            type: Array,
            required: false,
        },
    },
    data: function () {
        return {
            contentBlocks: this.value,
            contentBlocksCollapsed: false,
            contentBlocksCopiedFrom: null,
            debugStore: useDebugStore(),
            froalaStore: useFroalaStore(),
        };
    },
    mounted: function () {
        this.contentBlocks.map((contentBlock) => {
            contentBlock.collapsed = false;
            return contentBlock;
        });
    },
    computed: {
        displayNoContentBlocksMessage() {
            return !this.contentBlocks.length;
        },
    },
    methods: {
        toggleContentBlockCollapse: function (contentBlockIndex) {
            const contentBlocks = [...this.contentBlocks];
            contentBlocks[contentBlockIndex].collapsed = !contentBlocks[
                contentBlockIndex
            ].collapsed;
            this.contentBlocks = contentBlocks;
        },
        toggleAllContentBlocksCollapse: function () {
            const contentBlocks = [...this.contentBlocks];
            if (this.contentBlocksCollapsed) {
                contentBlocks.map(
                    (contentBlock) => (contentBlock.collapsed = true)
                );
            } else {
                contentBlocks.map(
                    (contentBlock) => (contentBlock.collapsed = false)
                );
            }
            this.contentBlocks = contentBlocks;
        },
        getContentBlocks: function (action) {
            axios
                .get(route("api.contentBlocks.index"), {
                    params: {
                        model_name: this.contentBlocksCopiedFrom.model_name,
                        id: this.contentBlocksCopiedFrom.id,
                    },
                })
                .then((response) => {
                    let contentBlocks = response.data.data.map(
                        (contentBlock) => {
                            return {
                                anchor: contentBlock.anchor,
                                anchor_text: contentBlock.anchor_text,
                                sections: contentBlock.sections.map(
                                    (contentSection) => {
                                        delete contentSection.content_block_id;
                                        return contentSection;
                                    }
                                ),
                                title: contentBlock.title,
                            };
                        }
                    );

                    this.$emit("load-content-blocks", {
                        action,
                        contentBlocks,
                    });
                })
                .catch((error) => {
                    console.log(error);
                    this.$notify({
                        group: "formError",
                        title: "Error Fetching Content Blocks",
                        text:
                            "Sorry there was an error fetching the content blocks",
                    });
                });
        },
    },
    watch: {
        value: function (newValue) {
            this.contentBlocks = newValue;
        },
        errors: function(newValue){
            Object.keys(newValue)
                .map(key => key.split('.'))
                .forEach(key => {
                    let field = key[0], index = key[1];
                    if (field === 'contentBlocks') {
                        this.$set(this.contentBlocks[index], 'collapsed', true);
                    }
                });
        },
        contentBlocksCopiedFrom: function (newValue) {
            if (newValue) {
                this.$confirm({
                    message: `Would you like to append or replace the content blocks?`,
                    button: {
                        no: "Replace",
                        yes: "Append",
                    },
                    callback: (append) => {
                        if (append) {
                            this.getContentBlocks('append');
                        } else {
                            this.$confirm({
                                message: `If you replace this element, your content will be lost and not recoverable. Are you sure you want to continue?`,
                                button: {
                                    yes: "Yes",
                                    no: "No",
                                },
                                callback: (replace) => {
                                    if (replace) {
                                        this.getContentBlocks('replace');
                                    }
                                },
                            });
                        }
                    },
                });
            }
        },
    },
};
</script>
